import React from 'react';
import type { useStudioTemplateChooser } from 'src/components/star-grid/hooks/useStudioTemplateChooser';
import type { StarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import styles from 'src/components/star-grid/components/styles/ReportStudio.module.scss';

interface Props {
  readonly selection: ReturnType<typeof useStudioTemplateChooser>;
  readonly text: StarGridText;
}

/**
 * The starting point as ONE dropdown: scratch, each template, then the saved reports to copy from.
 *
 * A native `select` with `optgroup` rather than tiles or a Cloudscape Select: the list carries a
 * heading ("Import existing Report") above the saved reports, and an optgroup is the only control that
 * renders a non-selectable heading inline without a custom popover. Tiles could not express the group
 * at all, and grew unusably wide once every saved report became an option.
 */
export const StudioTemplateChoices: React.FC<Props> = ({ selection, text }) => (
  <select
    className={styles.compactSelect}
    aria-label={text('studioTemplateLabel')}
    value={selection.value}
    onChange={(event) => selection.choose(event.target.value)}
    data-testid="studio-template"
  >
    {selection.groups.map((group, index) =>
      group.label ? (
        <optgroup key={group.label} label={group.label}>
          {group.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </optgroup>
      ) : (
        // The ungrouped head of the list (scratch + templates) needs no heading above it.
        <React.Fragment key={`group-${index}`}>
          {group.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </React.Fragment>
      )
    )}
  </select>
);
